/* components/patterns/PlanCard.jsx — карточка тарифа (унифицирована).
   // заменяет: SubscriptionTab.PlanCard, BusinessTariffPanel.PlanCard (фикс №15).
   Используется и в личном тарифе, и в бизнес-тарифе. Один компонент — два режима:
   isCurrent (текущий: статус доступа/период/автопродление/pending/usage) и опция выбора.
   Пропсы: plan, subscription, isCurrent, currentPrice, pendingPlanName,
           onUpgrade(slug), onDowngrade(slug), onCancelPending, busy.
   ХЕНДОФФ → frontend/components/common/PlanCard.jsx. */

import { Pill } from '../ui/Badge.jsx';
import { Button } from '../ui/Button.jsx';
import { Icon } from '../ui/Icon.jsx';
import { UsageBar } from '../ui/Metric.jsx';
import { fmt } from '../../lib/format.js';
import { accessNote } from './planAccess.js';
import { zeroPriceLabel } from './planPrice.js';

  // секунды → «2 ч 0 мин» / «10 мин»
  function fmtSeconds(s) {
    if (s == null) return '∞';
    const m = Math.round(s / 60);
    if (m >= 60) { const h = Math.floor(m / 60); return h + ' ч ' + (m % 60) + ' мин'; }
    return m + ' мин';
  }

  // Лимиты тарифа за период (-1 = безлимит, 0/нет = недоступно) → строки для карточки.
  function quotaText(plan) {
    const r = plan && plan.chat_messages_per_30d;
    const v = plan && plan.voice_seconds_per_30d;
    return {
      requests: r === -1 ? 'Безлимит' : (r == null || r === 0 ? '—' : Number(r).toLocaleString('ru-RU')),
      audio: v === -1 ? 'Безлимит' : (v == null || v === 0 ? '—' : fmtSeconds(v)),
    };
  }

  // МБ → «1 ГБ» / «512 МБ» (≥1024 МБ показываем в ГБ; дробь — до 1 знака).
  function fmtStorage(mb) {
    if (mb == null) return '∞';
    if (mb < 0) return 'Безлимит';
    if (mb >= 1024) { const gb = mb / 1024; return (Number.isInteger(gb) ? gb : gb.toFixed(1)) + ' ГБ'; }
    return Math.round(mb) + ' МБ';
  }

  // Бизнес-тарифы (фича team_members) несут базу знаний — у личных её нет,
  // поэтому лимиты файлов/объёма показываем только для них.
  function isBusinessPlan(plan) {
    return !!(plan && (plan.features || []).includes('team_members'));
  }

  // Лимиты базы знаний бизнес-тарифа → строки карточки (-1 = безлимит, 0/нет = —).
  function fileSpecs(plan) {
    const d = plan && plan.max_documents;
    const f = plan && plan.max_file_size_mb;
    return {
      documents: d === -1 ? 'Безлимит' : (d == null || d === 0 ? '—' : Number(d).toLocaleString('ru-RU')),
      storage: fmtStorage(plan && plan.max_storage_mb),
      fileSize: f === -1 ? 'Безлимит' : (f == null || f === 0 ? '—' : Math.round(f) + ' МБ'),
    };
  }

  // человекочитаемые подписи технических feature-слагов (иначе в карточке
  // тарифа всплывает «team_members»). Незнакомые строки показываем как есть.
  const FEATURE_LABELS = { team_members: 'Командный доступ с ролями', iiko_integration: 'Интеграция с iiko' };
  const featureLabel = (f) => FEATURE_LABELS[f] || f;

  /* Фичи, которые ЖИВУТ ТОЛЬКО в бизнес-пространстве: аналитика кассы и команда.
     На личной вкладке их обещать нельзя, даже если флаг у тарифа стоит — в личном
     пространстве ни кассы, ни участников нет. Именно это и вводило в заблуждение:
     бесплатный «Демо» показывал «✓ Интеграция с iiko», а платные Start/Expert —
     ничего, и экран выглядел как предложение заплатить и потерять функцию.
     Скрываем ПО ВКЛАДКЕ, а не по тарифу: тот же `demo` висит и на бизнес-
     пространствах, где интеграция настоящая. */
  const BUSINESS_ONLY_FEATURES = ['iiko_integration', 'team_members'];
  // Чего в личном тарифе нет — говорим прямо, а не умолчанием. Умолчание и
  // привело к тому, что рестораны платили за ассистента, ожидая аналитику.
  const PERSONAL_MISSING = [
    'Аналитика кассы: выручка, ОПиУ, ABC',
    'Командный доступ и общие данные',
  ];

  // период оплаты → суффикс цены. Цена в таблице — за весь срок, не за месяц,
  // поэтому для длинных тарифов нельзя писать «/ мес».
  function periodSuffix(days) {
    if (!days || days === 30) return '/ мес';
    if (days === 7) return '/ 7 дней';
    if (days === 60) return '/ 2 мес';
    if (days === 180) return '/ 6 мес';
    if (days === 360 || days === 365) return '/ год';
    return '/ ' + days + ' дн';
  }

  // Подпись запланированного перехода. Платный тариф дешевле включается ОПЛАТОЙ
  // продления по его цене (17.10.2026: раньше он включался в конце срока даром),
  // поэтому говорим, что и когда спишут: карта — сама, счёт — придёт на почту.
  // Дата — конец СРОКА (`expires_at`): `period.end` — окно квот, у годового тарифа
  // оно кончается через 30 дней. Старый ответ без цены/канала — прежняя фраза.
  function pendingNote(subscription) {
    const end = subscription.expires_at || (subscription.period && subscription.period.end);
    const when = fmt.date(end);
    const price = Number(subscription.pending_plan_price);
    const amount = Number.isFinite(price) && price > 0 ? fmt.money(price) : null;
    if (amount && !subscription.expires_at) {
      // Платный тариф без срока (договор, ручная выдача): продления нет, и переход не
      // выполнится никогда. Дату окна квот (`period.end`) окончанием не называем.
      return 'У текущего тарифа нет срока окончания, поэтому переход не выполнится. '
        + 'Отмените его или напишите в поддержку.';
    }
    if (amount && subscription.auto_renew === false) {
      // Продление выключено (переход запланирован до 17.10.2026 или продление выключили
      // потом): платить за новый тариф никто не будет, и он не включится — срок кончится.
      // «Отменить» снова включает продление ТЕКУЩЕГО тарифа — говорим об этом прямо.
      return 'Продление выключено, поэтому переход не состоится: ' + when + ' подписка '
        + 'закончится. Чтобы перейти, отмените переход (продление текущего тарифа '
        + 'включится) и выберите тариф заново.';
    }
    if (amount && subscription.renewal_channel === 'card') {
      return 'Спишем ' + amount + ' с привязанной карты ' + when + ' — после оплаты тариф '
        + 'сменится. До этого действует текущий.';
    }
    if (amount && subscription.renewal_channel === 'invoice') {
      return 'Счёт на ' + amount + ' придёт на почту за несколько дней до ' + when
        + ' — тариф сменится после его оплаты. До этого действует текущий.';
    }
    if (amount) {
      return 'Тариф сменится после оплаты ' + amount + '. До ' + when + ' действует текущий.';
    }
    return 'Вступит в силу с ' + when + '. До этого действует текущий тариф.';
  }

  // Акция с очередью (`starts_after_current`) встанет за текущим тарифом → дата её
  // старта (конец срока текущего), иначе null. Зеркало `billing.queues_after_current`:
  // идёт оплаченный срок ДРУГОГО платного тарифа — любого, цену не сравниваем (решение
  // владельца 19.10.2026: и за Start — очередь, Start дорабатывает срок, а не «сгорает»
  // апгрейдом с зачётом остатка). `currentPrice` — только признак «текущий тариф
  // платный» (0 у демо/бесплатного). Одно место на карточку, окно способа оплаты и
  // страницу /buy — иначе одно пообещает «начнётся», а другое «сразу».
  function queuedStartsAt(plan, subscription, currentPrice) {
    if (!plan || !plan.starts_after_current || !subscription) return null;
    if (subscription.plan && subscription.plan === plan.slug) return null;
    if (!subscription.is_active || !subscription.expires_at) return null;
    if (new Date(subscription.expires_at) <= new Date()) return null;
    if (!(currentPrice > 0)) return null;
    return subscription.expires_at;
  }

  // scope — ВКЛАДКА, на которой показана карточка ('personal' | 'business').
  // Не свойство тарифа: один и тот же `demo` живёт и там и там, но обещать
  // аналитику кассы можно только в бизнес-пространстве.
  /* «Продление: X ₽» на карточке ТЕКУЩЕГО платного тарифа — только когда цена
     продления клиента отличается от цены тарифа (после смены цены ему закреплена
     прежняя, billing.renewal_price), автопродление включено и перехода нет. Без этой строки
     карточка показывала прайс, а списывалось другое — по жалобе не разобраться. */
  function renewalPriceLine(plan, subscription, isCurrent) {
    if (!isCurrent || !plan || !plan.is_paid || !subscription || !subscription.auto_renew) return '';
    // Запланирован переход: следующий период спишут по НОВОМУ тарифу (сумму называет
    // плашка перехода, billing.renewal_terms), и цена продления текущего — уже не то,
    // что спишут. Раньше переход выключал продление, и строка пряталась сама.
    if (subscription.pending_plan) return '';
    const own = subscription.renewal_price_rub;
    if (own == null || !Number.isFinite(Number(own))) return '';
    if (Number(own) === Number(plan.price_rub)) return '';
    return fmt.money(Number(own)) + ' ' + periodSuffix(plan.period_days);
  }

  /* Цена, ОТ которой считается «дороже/дешевле» и доплата: то, что клиент реально
     платит за текущий тариф (`renewal_price_rub` из /me — закреплённая цена после
     смены прайса), а не прайс тарифа. Зеркало сервера: направление перехода и
     пропорция там тоже считаются от billing.renewal_price. Без закрепления (или на
     старом бэкенде) — прайс текущего тарифа, как раньше. */
  function currentPriceOf(currentPlan, subscription) {
    const list = currentPlan ? Number(currentPlan.price_rub) || 0 : 0;
    const own = subscription ? subscription.renewal_price_rub : null;
    if (list > 0 && own != null && Number.isFinite(Number(own))) return Number(own);
    return list;
  }

  function PlanCard({ plan, subscription, isCurrent = false, currentPrice = 0,
                     pendingPlanName, onUpgrade, onDowngrade, onCancelPending, onSetAutoRenew,
                     busy = false, showAction = true, actionLabel, scope = 'business' }) {
    const price = plan.price_rub;
    const priceText = price === 0 ? zeroPriceLabel(plan) : (fmt.money(price) + ' ' + periodSuffix(plan.period_days));
    const specs = quotaText(plan);
    const business = isBusinessPlan(plan);
    const shownFeatures = (plan.features || []).filter(
      (f) => !(scope === 'personal' && BUSINESS_ONLY_FEATURES.includes(f)));
    const files = business ? fileSpecs(plan) : null;
    // Акция с очередью (`starts_after_current`) всегда идёт через оплату: при той же
    // цене, что у текущего тарифа, «вниз» увело бы её в бесплатный переход с конца
    // периода, а его бэк отбивает. Если у текущего платного тарифа (любого, и у Start)
    // идёт срок — акция начнётся после него, об этом и говорит кнопка.
    const queuedFrom = isCurrent ? null : queuedStartsAt(plan, subscription, currentPrice);
    const direction = (price > currentPrice || plan.starts_after_current) ? 'up' : 'down';
    const pending = isCurrent && subscription && subscription.pending_plan;
    // Отложенный тариф уже оплачен (акция в очереди): не «переход», а покупка,
    // и кнопки «Отменить» у неё нет — бэк такую отмену отбивает (prepaid_pending).
    const prepaid = !!(pending && subscription.pending_plan_prepaid);
    // Отложенный тариф включается в конце СРОКА подписки (`expires_at`), а не в
    // конце 30-дневного окна квоты: у годового тарифа это разные даты.
    const pendingFrom = subscription
      && (subscription.expires_at || (subscription.period && subscription.period.end));
    // Разовый тариф (акция 1=2, «Платное демо»: renewable=false) не продлевается вовсе:
    // «Возобновить» сервер отбивает (`not_renewable`), кроны такой тариф не берут, и флаг
    // подписки (даже включённый прежней дырой) ничего не значит. Тариф без поля (старый
    // ответ) считаем продлеваемым — как раньше. Разовую ПРОДАЖУ обычного тарифа (ссылка
    // без продления) отсюда не видно: её отказ приходит с сервера текстом (`detail`).
    const oneTime = plan.renewable === false;
    // Управление автопродлением — только на текущем ПЛАТНОМ продлеваемом плане без
    // запланированного перехода (и без оплаченной акции в очереди — это тоже `pending`):
    // включено → «Отменить продление», выключено → «Возобновить». На демо/бесплатном и
    // разовом продлевать нечего (кнопок нет).
    const canRenewToggle = isCurrent && plan.is_paid && !!onSetAutoRenew && !pending && !oneTime;
    const showCancelRenew = canRenewToggle && subscription && subscription.auto_renew;
    const showResumeRenew = canRenewToggle && subscription && !subscription.auto_renew;
    // Доступ закрыт (тариф не выбран / бесплатный период или оплата закончились) —
    // говорим об этом на карточке ТЕКУЩЕГО тарифа (строка живёт в его блоке ниже;
    // карточкам выбора та же подписка приходит, но статус доступа — не про них).
    // Нет `access` (старый бэкенд) или доступ открыт → null, карточка как раньше
    // (см. planAccess.js).
    const accessText = accessNote(subscription && subscription.access);
    // При закрытом доступе список «✓ фич» на ТЕКУЩЕЙ карточке не рисуем: «✓ Интеграция
    // с iiko» прямо над «Тариф не выбран… недоступны» — карточка сама себе противоречит
    // (фича у тарифа есть, отказ даёт состояние доступа). Всё объясняет строка статуса.
    // Условие — сама строка (accessText), а не access.ok: незнакомое состояние строки
    // не даёт, и карточка остаётся прежней. Карточки выбора — про другие тарифы, их
    // список не трогаем.
    const hideFeatures = isCurrent && !!accessText;
    const renewalLine = renewalPriceLine(plan, subscription, isCurrent);
    // actionLabel переопределяет текст кнопки (напр. карточка-семейство «Выбрать период оплаты»).
    const ctaLabel = actionLabel || (queuedFrom
      ? 'Оплатить — начнётся ' + fmt.date(queuedFrom)
      : 'Перейти на ' + plan.name);
    // Последний день продаж акции: граница окна не включительна (00:00 следующего дня).
    const saleLastDay = !isCurrent && plan.sale_ends_at
      ? new Date(new Date(plan.sale_ends_at).getTime() - 1) : null;

    let action = null;
    if (isCurrent) {
      action = <Button variant="secondary" size="md" disabled iconLeft="check">Текущий тариф</Button>;
    } else if (direction === 'up') {
      action = <Button variant="primary" size="md" loading={busy} disabled={busy} onClick={() => onUpgrade && onUpgrade(plan.slug)}>{ctaLabel}</Button>;
    } else {
      action = <Button variant="secondary" size="md" loading={busy} disabled={busy} onClick={() => onDowngrade && onDowngrade(plan.slug)}>{ctaLabel}</Button>;
    }

    return (
      <section className={'plan-card' + (isCurrent ? ' is-current' : '') + (plan.is_paid ? ' plan-card--paid' : '')}>
        <div className="plan-card__head">
          <div className="plan-card__id">
            <div className="plan-card__name t-h4">{plan.name}</div>
            <div className="plan-card__price num">{priceText}</div>
            {saleLastDay && (
              <div className="plan-card__sale t-caption text-dim">Акция до {fmt.date(saleLastDay, { timeZone: 'Europe/Moscow' })} включительно</div>
            )}
          </div>
          {isCurrent && <Pill tone="accent" glyph="check">Текущий</Pill>}
        </div>

        {/* Лимиты тарифа: на текущем плане их показывают usage-бары ниже (used / limit),
            поэтому отдельный спек-блок рисуем только на карточках выбора. */}
        {!isCurrent && (
          <ul className="plan-card__specs">
            <li><Icon name="chat" size={14} /><span className="spec-key">Запросы</span><span className="spec-val num">{specs.requests}</span></li>
            <li><Icon name="microphone" size={14} /><span className="spec-key">Аудио</span><span className="spec-val num">{specs.audio}</span></li>
            {files && <>
              <li><Icon name="file" size={14} /><span className="spec-key">Документы базы знаний</span><span className="spec-val num">{files.documents}</span></li>
              <li><Icon name="box" size={14} /><span className="spec-key">Объём базы знаний</span><span className="spec-val num">{files.storage}</span></li>
              <li><Icon name="upload" size={14} /><span className="spec-key">Размер одного файла</span><span className="spec-val num">{files.fileSize}</span></li>
            </>}
          </ul>
        )}

        {!hideFeatures && shownFeatures.length > 0 && (
          <ul className="plan-card__features">
            {shownFeatures.map((f, i) => (
              <li key={i}><Icon name="check" size={14} /><span>{featureLabel(f)}</span></li>
            ))}
          </ul>
        )}

        {/* Что в тариф НЕ входит — только на личной вкладке и только у платных.
            У бесплатного «Демо» этот список был бы придиркой: за него не платят. */}
        {scope === 'personal' && price > 0 && (
          <ul className="plan-card__features plan-card__features--missing">
            {PERSONAL_MISSING.map((f, i) => (
              <li key={i}><Icon name="close" size={14} /><span>{f}</span></li>
            ))}
          </ul>
        )}

        {isCurrent && subscription && (
          <div className="plan-card__current">
            {accessText && (
              <div className="callout callout--warn plan-card__access" role="status">
                <Icon name="alert-triangle" size={16} />
                <div className="grow">{accessText}</div>
              </div>
            )}

            <div className="plan-card__meta">
              {/* пустой expires_at (бессрочный демо/free) не рисуем как «1 янв 1970» */}
              <span className="extra-metric"><span className="extra-metric__key t-caption">Действует до</span><span className="extra-metric__val num">{subscription.expires_at ? fmt.date(subscription.expires_at) : '—'}</span></span>
              {/* Автопродление — свойство ПЛАТНОГО тарифа. У бесплатного флаг подписки
                  по умолчанию true, и карточка «Демо» писала «Автопродление: Включено»,
                  хотя продлевать и списывать там нечего. */}
              {plan.is_paid && (
                <span className="extra-metric"><span className="extra-metric__key t-caption">Автопродление</span><span className="extra-metric__val">{oneTime ? 'Нет, разовый тариф' : (subscription.auto_renew ? 'Включено' : 'Выключено')}</span></span>
              )}
              {renewalLine && (
                <span className="extra-metric"><span className="extra-metric__key t-caption">Продление</span><span className="extra-metric__val num">{renewalLine}</span></span>
              )}
            </div>

            {(showCancelRenew || showResumeRenew) && (
              <div className="plan-card__renew">
                {showCancelRenew && (
                  <Button variant="ghost" size="sm" iconLeft="close" loading={busy} disabled={busy}
                    onClick={() => onSetAutoRenew(false)}>Отменить продление подписки</Button>
                )}
                {showResumeRenew && (
                  <Button variant="ghost" size="sm" iconLeft="refresh" loading={busy} disabled={busy}
                    onClick={() => onSetAutoRenew(true)}>Возобновить продление</Button>
                )}
              </div>
            )}

            {pending && prepaid && (
              <div className="callout callout--info plan-card__pending">
                <Icon name="check" size={16} />
                <div className="grow">
                  <div className="t-label">Оплачен тариф «{pendingPlanName || subscription.pending_plan}»</div>
                  <div className="t-caption text-dim">Начнёт действовать {fmt.date(pendingFrom)}, когда закончится текущий тариф.</div>
                </div>
              </div>
            )}
            {pending && !prepaid && (
              <div className="callout callout--warn plan-card__pending">
                <Icon name="alert-triangle" size={16} />
                <div className="grow">
                  <div className="t-label">Переход на «{pendingPlanName || subscription.pending_plan}»</div>
                  <div className="t-caption text-dim" data-testid="plan-pending-note">{pendingNote(subscription)}</div>
                </div>
                <Button variant="ghost" size="sm" loading={busy} disabled={busy} onClick={onCancelPending}>Отменить</Button>
              </div>
            )}

            {subscription.usage && subscription.limits && (
              <div className="plan-card__usage stack stack-12">
                <UsageBar label="Запросы" used={subscription.usage.chat_messages_used} limit={subscription.limits.chat_messages_per_30d} unit="" />
                <UsageBar label="Аудио" used={subscription.usage.voice_seconds_used} limit={subscription.limits.voice_seconds_per_30d} fmt={fmtSeconds} />
                {business && subscription.usage.documents_used != null && (
                  <UsageBar label="Документы базы знаний" used={subscription.usage.documents_used}
                    limit={subscription.limits.max_documents === -1 ? null : subscription.limits.max_documents} unit="" />
                )}
                {business && subscription.usage.storage_mb_used != null && (
                  <UsageBar label="Объём базы знаний" used={subscription.usage.storage_mb_used}
                    limit={subscription.limits.max_storage_mb === -1 ? null : subscription.limits.max_storage_mb} fmt={fmtStorage} />
                )}
              </div>
            )}
          </div>
        )}

        {showAction && <div className="plan-card__foot">{action}</div>}
      </section>
    );
  }

export { PlanCard };
export { fmtSeconds };
export { quotaText };
export { fmtStorage, fileSpecs, isBusinessPlan };
export { renewalPriceLine, currentPriceOf };
export { periodSuffix };
export { queuedStartsAt };
